import { WidgetDataType, WidgetNoneType } from '@atlas-metrics/microsite-types';
import { TFunction } from 'next-i18next';
import { Fragment } from 'react';

const getNumberLocaleByLanguage = (language: string): string => {
  switch (language) {
    case 'de_DE':
      return 'de-DE';
    case 'en_EN':
    default:
      return 'en-US';
  }
};

export const formatIntByLocale = (
  value: string | number,
  locale: string
): string | number | Intl.NumberFormatPart[] => {
  const asNumber = Number(String(value));
  const numberFormat = new Intl.NumberFormat(getNumberLocaleByLanguage(locale));
  const formattedValue =
    isNaN(asNumber) || (!value && value !== 0)
      ? value
      : numberFormat.formatToParts(asNumber);

  return formattedValue;
};

export const formatFloatByLocale = (
  value: string | number,
  locale: string
): string | number | Intl.NumberFormatPart[] => {
  const asNumber = Number(String(value).replace(',', '.'));
  const numberFormat = new Intl.NumberFormat(
    getNumberLocaleByLanguage(locale),
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );
  const formattedValue =
    (!value && value !== 0) || isNaN(asNumber)
      ? value
      : numberFormat.formatToParts(asNumber);

  // according to IEEE standard -0 is a legit result so wee need to handle this manually
  // https://en.wikipedia.org/wiki/IEEE_754
  if (formattedValue === '-0.00') {
    return '0.00';
  }
  return formattedValue;
};

const formatNumber = (
  formattedValue: string | number | Intl.NumberFormatPart[]
): React.ReactNode => {
  // to prevent browser from breaking number at wrong place, we are using the <wbr/> tag to add word break opportunities after separator between the integers and fractional parts.

  if (Array.isArray(formattedValue))
    return formattedValue.map((part, idx) =>
      ['fraction', 'integer', 'exponentInteger'].includes(part.type) ? (
        part.value
      ) : (
        <Fragment key={idx}>
          {part.value}
          <wbr />
        </Fragment>
      )
    );

  return formattedValue;
};

const useFormatValue = (
  value: string | number | boolean | null,
  dataType: WidgetDataType | WidgetNoneType,
  t: TFunction,
  language: string
): string | number | React.ReactNode | null => {
  if (value == undefined || dataType == undefined) {
    return value;
  }

  switch (typeof value) {
    case 'boolean':
      return value ? t('boolean.true') : t('boolean.false');
    case 'number':
      if (dataType === 'FLOAT_2') {
        return formatNumber(formatFloatByLocale(value, language));
      }
      if (dataType === 'INTEGER') {
        return formatNumber(formatIntByLocale(value, language));
      }

      // other number like year should not be formatted
      return value;
    default:
      return value;
  }
};

export type FormatValueFn = (
  value: string | number | boolean | null,
  dataType: WidgetDataType | WidgetNoneType
) => string | number | React.ReactNode | null;

export default useFormatValue;
